import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import get from 'lodash/get';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@modernhealth/vital-components';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { useStartCareRecommendationMutation } from 'src/modules/onboarding/documents/startCareRecommendationMutation.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import marginaliaDeliveryImg from 'src/public/img/marginalia-delivery.svg';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { color } from 'src/shared/styles/variables';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    flexDirection: 'column',
    textAlign: 'center',
});

const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '16em',
    cursor: 'pointer',
    marginRight: 12,
});

const PageTitle = styled.h1({
    fontWeight: 800,
    fontSize: '28px',
    lineHeight: '32px',
    color: color.ebonyClay,
});

const PageDescription = styled.p({
    fontWeight: 400,
    width: '336px',
    fontSize: '16px',
    lineHeight: '24px',
    color: color.blueBayoux,
    marginBottom: '40px',
    marginTop: '24px',
});
const ButtonsContainer = styled.div({
    width: '240px',
    justifyContent: 'center',
    display: 'flex',
});

export const CompletionPage: React.FC = () => {
    const { t } = useTranslation();
    const { showRegistrationSurveyWeb } = useFlags();
    const userInformation = useSelector(getUserInformation);
    const firstName = userInformation.firstName;
    const pageTitle = t('auth:completion_page.title', { firstName });
    const [loading, setLoading] = useState(false);
    const [startCareRecommendation] = useStartCareRecommendationMutation({
        client: appClient,
    });

    const dispatch: Dispatch = useDispatch();

    const redirectToStartAssessment = async () => {
        try {
            setLoading(true);
            // kick off a new care recommendation that will be updated through out the onboarding flow
            const res = await startCareRecommendation();
            const careUuid = get(res, 'data.startCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Did not receive care uuid');
            }

            await dispatch.user.fetchUserInfo();
            navigationService.navigate(
                showRegistrationSurveyWeb ? '/registration-survey' : '/start-assessment-page',
            );
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to create a care recommendation uuid');

                Sentry.captureException(error);
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <img src={marginaliaDeliveryImg} alt="" />
                <PageTitle>{pageTitle}</PageTitle>
                <PageDescription>{t('auth:completion_page.description')}</PageDescription>
                <ButtonsContainer>
                    <Button
                        onClick={redirectToStartAssessment}
                        variant="primary"
                        busy={loading}
                        size="large"
                        fluid
                    >
                        {t('auth:completion_page.button.continue')}
                    </Button>
                </ButtonsContainer>
            </Container>
        </>
    );
};
